/**
 * F 472 — THE GLOBAL "+".
 *
 * ONE button that ASKS what you want to make: a page, a database, or a task.
 * It is the discoverable entrance to all three; the per-section "+"s — one on
 * each section heading, before the list — are the direct entrances.
 *
 * Why it is its own component: the button lives in the Workspace header
 * (WorkspaceSurface.tsx), which is a different component from the sidebar that
 * owns the actions. Splitting it out keeps the menu testable on its own.
 *
 * It speaks exactly ONE event, `fc:sidebar:create`, and SidebarV2 routes it to
 * the same three handlers its own section buttons call. Two entrances, one
 * implementation; they cannot drift.
 *
 * WHY IT IS NOT A MANTINE `Menu`:
 *
 *  1. Mantine's `Menu` cannot be wrapped in a `Tooltip` — `Tooltip` passes a
 *     ref to its child and `Menu` is a function component that does not
 *     forward one, so React logs "Function components cannot be given refs"
 *     and the tooltip never anchors.
 *  2. With the Tooltip INSIDE `Menu.Target`, the mirror problem appears: the
 *     Menu's onClick lands on the Tooltip, and the Tooltip does not pass it
 *     down, so the dropdown never opens on click. Measured, not assumed.
 *  3. Measured in this repo's jsdom: opening a Mantine `Menu` takes minutes
 *     — the Popover/Floating machinery does not settle without a layout
 *     engine. A menu that cannot be tested is a menu nobody will test.
 *
 * So the popover is hand-rolled: a plain button, a plain list, the sidebar's
 * OWN CSS tokens, and no portal — which also means it cannot escape the
 * sidebar's scroll container, so it is anchored to its own button.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconPlus } from '@tabler/icons-react';
import { eventBus } from '../../../core/event-bus';
import { SIDEBAR_CREATE_EVENT, type SidebarCreateKind } from './sidebarCreateEvent';

const CHOICES: Array<{ kind: SidebarCreateKind; label: string; testId: string }> = [
  { kind: 'page', label: 'New page', testId: 'fc-sidebar-create-page' },
  { kind: 'database', label: 'New database', testId: 'fc-sidebar-create-database' },
  { kind: 'task', label: 'New task', testId: 'fc-sidebar-create-task' },
];

export const SidebarCreateMenu: React.FC = () => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const firstItemRef = useRef<HTMLButtonElement | null>(null);

  const close = useCallback(() => setOpen(false), []);

  // Outside click and Escape both close it. `mousedown` (not `click`) so the
  // menu is already gone by the time a click lands somewhere else.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  // Move focus into the list when it opens, so the keyboard follows the eye.
  useEffect(() => {
    if (open) firstItemRef.current?.focus();
  }, [open]);

  const request = (kind: SidebarCreateKind) => {
    eventBus.emit(SIDEBAR_CREATE_EVENT, { kind });
    close();
  };

  return (
    <div className="fc-create-menu" ref={wrapRef}>
      <button
        type="button"
        className="fc-create-menu__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Add a page, a database or a task"
        title="Add — page, database or task"
        data-testid="fc-sidebar-create-menu"
      >
        <IconPlus size={16} />
      </button>
      {open && (
        <div
          className="fc-create-menu__list"
          role="menu"
          aria-label="Add"
          ref={listRef}
          data-testid="fc-sidebar-create-list"
        >
          {CHOICES.map((choice, i) => (
            <button
              key={choice.kind}
              type="button"
              role="menuitem"
              className="fc-create-menu__item"
              onClick={() => request(choice.kind)}
              data-testid={choice.testId}
              ref={i === 0 ? firstItemRef : undefined}
            >
              <IconPlus size={14} />
              <span>{choice.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SidebarCreateMenu;
